import React from "react";
import { RefreshCw, Upload, Smartphone, Link2, Check, Download, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import {
  APP_CHOICES,
  listAppReleases,
  publishAppRelease,
  updateAppRelease,
  listAppCrashes,
  type AppId,
  type AppRelease,
  type AppReleaseStatus,
  type AppCrash
} from "../services/api";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * App Releases — the Android APK catalog operators publish builds to, one
 * release line per app: Orbit Sales (the rep app), Orbit Inventory and Orbit
 * Tenant (tenant plan phase 57). Each upload proxies through
 * platform-admin-service to config-service (which stores the binary +
 * metadata). A release's status governs what that app does:
 *   • active     — the normal live version.
 *   • deprecated — reps on it are nudged to update (soft prompt).
 *   • blocked    — reps on that version or older are forced to update.
 * "Force update (mandatory)" makes a specific version a hard-required update.
 */

const PLATFORM = "android";

const APP_LABEL: Record<AppId, string> = Object.fromEntries(APP_CHOICES.map((a) => [a.id, a.label])) as Record<AppId, string>;

/** Human-readable file size. */
function fmtBytes(n: number): string {
  if (!n || n < 1024) return (n || 0) + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function statusPillClass(s: AppReleaseStatus): string {
  switch (s) {
    case "active": return "success";
    case "deprecated": return "warn";
    case "blocked": return "danger";
    default: return "";
  }
}

const STATUS_LABELS: Record<AppReleaseStatus, string> = {
  active: "Active",
  deprecated: "Deprecated",
  blocked: "Blocked"
};

/**
 * Copy to clipboard, with a fallback for the non-secure-context case
 * (navigator.clipboard is undefined outside https/localhost, which is exactly
 * where someone testing against an IP would hit it).
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea approach
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * "Copy install link" for one release.
 *
 * The URL is the gateway's public /api/app/releases/:platform/:versionCode/apk
 * route, which needs no auth — so this link can go straight into a WhatsApp
 * group and every rep can install from it. Keep that in mind before sharing a
 * blocked or internal build: there is no token on it and nothing expires.
 */
function ShareLink({ rel }: { rel: AppRelease }) {
  const toast = useToast();
  const [copied, setCopied] = React.useState(false);
  const url = rel.publicApkUrl;

  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!url) return null;

  return (
    <>
      <button
        className="btn btn-ghost btn-sm"
        title={`Copy the public install link\n${url}`}
        onClick={async () => {
          const ok = await copyText(url);
          if (ok) {
            setCopied(true);
            toast.push("success", "Install link copied. Anyone with it can install this build.");
          } else {
            toast.push("error", "Couldn't copy. The link is in the button tooltip.");
          }
        }}
      >
        {copied ? <Check size={14} /> : <Link2 size={14} />} {copied ? "Copied" : "Copy link"}
      </button>
      <a
        className="btn btn-ghost btn-sm"
        href={url}
        target="_blank"
        rel="noreferrer"
        title="Download this APK"
      >
        <Download size={14} />
      </a>
    </>
  );
}

export function AppReleasesPage() {
  usePageTitle("App Releases");
  const toast = useToast();

  // Which app's release line is on screen. Each app counts its builds from its own start.
  const [app, setApp] = React.useState<AppId>("sales");
  const [releases, setReleases] = React.useState<AppRelease[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // versionCode currently being mutated (status/mandatory) — disables its row controls.
  const [busyVersion, setBusyVersion] = React.useState<number | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAppReleases(PLATFORM, app);
      setReleases(res.releases ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load releases.");
    } finally {
      setLoading(false);
    }
  }, [app]);

  React.useEffect(() => { void load(); }, [load]);

  const onPublished = React.useCallback(() => { void load(); }, [load]);

  const setStatus = async (rel: AppRelease, status: AppReleaseStatus) => {
    if (rel.status === status) return;
    setBusyVersion(rel.versionCode);
    try {
      const updated = await updateAppRelease(rel.app ?? app, rel.platform, rel.versionCode, { status });
      setReleases((prev) => prev.map((r) => (r.versionCode === rel.versionCode ? updated : r)));
      toast.push("success", `${rel.versionName} set to ${STATUS_LABELS[status]}.`);
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Could not update the release.");
    } finally {
      setBusyVersion(null);
    }
  };

  const toggleMandatory = async (rel: AppRelease) => {
    setBusyVersion(rel.versionCode);
    try {
      const updated = await updateAppRelease(rel.app ?? app, rel.platform, rel.versionCode, { mandatory: !rel.mandatory });
      setReleases((prev) => prev.map((r) => (r.versionCode === rel.versionCode ? updated : r)));
      toast.push("success", updated.mandatory ? `${rel.versionName} is now a forced update.` : `${rel.versionName} is no longer forced.`);
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Could not update the release.");
    } finally {
      setBusyVersion(null);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>App Releases</h1>
          <p className="muted">
            Publish Android builds of each Orbit app and control which version people run.
            Active is the normal live version, Deprecate nudges people to update, Block forces
            anyone on that version or older to update. Each app has its own builds and its own floor.
          </p>
        </div>
        <div className="page-actions">
          <div className="row" role="tablist" aria-label="App" style={{ gap: 4 }}>
            {APP_CHOICES.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={app === a.id}
                className={`btn btn-sm ${app === a.id ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setApp(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : undefined} /> Refresh
          </button>
        </div>
      </header>

      <PublishForm app={app} onPublished={onPublished} />

      {error ? (
        <div className="card" style={{ borderColor: "rgba(255,92,122,0.3)" }}>
          <p style={{ color: "var(--danger)", margin: 0 }}>{error}</p>
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Mandatory</th>
              <th>Size</th>
              <th>Published</th>
              <th>Notes</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && releases.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><p className="muted">Loading releases…</p></div></td></tr>
            ) : releases.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><p className="muted">No {APP_LABEL[app]} releases published yet.</p></div></td></tr>
            ) : (
              releases.map((rel) => {
                const busy = busyVersion === rel.versionCode;
                return (
                  <tr key={rel.versionCode}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{rel.versionName}</div>
                      <div className="faint mono" style={{ fontSize: 11 }}>code {rel.versionCode}</div>
                    </td>
                    <td><span className={`pill ${statusPillClass(rel.status)}`}>{STATUS_LABELS[rel.status]}</span></td>
                    <td>{rel.mandatory ? <span className="pill danger">Forced</span> : <span className="faint">No</span>}</td>
                    <td>{fmtBytes(rel.sizeBytes)}</td>
                    <td>
                      <div>{fmtDate(rel.publishedAt)}</div>
                      {rel.publishedBy ? <div className="faint" style={{ fontSize: 11 }}>{rel.publishedBy}</div> : null}
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      <div
                        className="muted"
                        title={rel.releaseNotes || undefined}
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5 }}
                      >
                        {rel.releaseNotes || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <ShareLink rel={rel} />
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy || rel.status === "active"}
                          onClick={() => void setStatus(rel, "active")}
                        >
                          Active
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy || rel.status === "deprecated"}
                          onClick={() => void setStatus(rel, "deprecated")}
                        >
                          Deprecate
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy || rel.status === "blocked"}
                          onClick={() => void setStatus(rel, "blocked")}
                        >
                          Block
                        </button>
                        <button
                          className="btn btn-sm"
                          disabled={busy}
                          onClick={() => void toggleMandatory(rel)}
                          title="Force reps on this exact version to update"
                        >
                          {rel.mandatory ? "Unset forced" : "Force update"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <CrashesCard />
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * App crashes — stack traces uploaded by the phone's own crash handler.
 * --------------------------------------------------------------------------*/

function CrashesCard() {
  const [crashes, setCrashes] = React.useState<AppCrash[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAppCrashes(200);
      setCrashes(res.crashes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load crashes.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  return (
    <div className="card" style={{ padding: 20, marginTop: 20 }}>
      <div className="section-head row between" style={{ marginBottom: 12, alignItems: "center" }}>
        <h2 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={16} /> App crashes
        </h2>
        <button className="btn btn-ghost btn-sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw size={14} className={loading ? "spin" : undefined} /> Refresh
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
        Uploaded automatically by each Orbit app when it crashes (on the next launch). Newest first.
      </p>
      {error ? <p style={{ color: "var(--danger)" }}>{error}</p> : null}
      {loading && crashes.length === 0 ? (
        <p className="muted">Loading crashes…</p>
      ) : crashes.length === 0 ? (
        <div className="empty-state"><p className="muted">No crashes reported. As it should be.</p></div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                <th>When</th>
                <th>App</th>
                <th>Summary</th>
                <th>Version</th>
                <th>Device</th>
                <th>Rep</th>
              </tr>
            </thead>
            <tbody>
              {crashes.map((c) => {
                const open = openId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr onClick={() => setOpenId(open ? null : c.id)} style={{ cursor: "pointer" }}>
                      <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtDate(c.crashedAt || c.createdAt)}</td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>{APP_LABEL[c.app ?? "sales"]}</td>
                      <td style={{ maxWidth: 380 }}>
                        <div className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                          {c.summary || "(no summary)"}
                        </div>
                      </td>
                      <td>{c.versionName || "—"}{c.versionCode != null ? <span className="faint" style={{ fontSize: 11 }}> ({c.versionCode})</span> : null}</td>
                      <td style={{ fontSize: 12.5 }}>{c.device || "—"}{c.osVersion ? <span className="faint"> · {c.osVersion}</span> : null}</td>
                      <td style={{ fontSize: 12.5 }}>{c.userEmail || "—"}</td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={7}>
                          <pre className="mono" style={{ fontSize: 11.5, whiteSpace: "pre-wrap", maxHeight: 320, overflow: "auto", margin: 0, padding: 12, background: "rgba(0,0,0,0.25)", borderRadius: 8 }}>
                            {c.stack}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Publish form — file + version metadata. Builds a FormData and posts it.
 * --------------------------------------------------------------------------*/

function PublishForm({ app, onPublished }: { app: AppId; onPublished: () => void }) {
  const toast = useToast();
  const fileRef = React.useRef<HTMLInputElement>(null);

  const [file, setFile] = React.useState<File | null>(null);
  const [versionCode, setVersionCode] = React.useState("");
  const [versionName, setVersionName] = React.useState("");
  const [releaseNotes, setReleaseNotes] = React.useState("");
  const [mandatory, setMandatory] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const reset = () => {
    setFile(null);
    setVersionCode("");
    setVersionName("");
    setReleaseNotes("");
    setMandatory(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const canSubmit =
    !submitting && !!file && versionCode.trim() !== "" && Number.isInteger(Number(versionCode)) && versionName.trim() !== "";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { toast.push("error", "Choose an APK file first."); return; }
    if (!canSubmit) return;

    const form = new FormData();
    form.append("apk", file);
    form.append("versionCode", String(parseInt(versionCode, 10)));
    form.append("versionName", versionName.trim());
    form.append("releaseNotes", releaseNotes.trim());
    form.append("mandatory", mandatory ? "true" : "false");
    form.append("platform", PLATFORM);
    form.append("app", app);

    setSubmitting(true);
    try {
      const rel = await publishAppRelease(form);
      toast.push("success", `Published ${APP_LABEL[app]} ${rel.versionName} (code ${rel.versionCode}).`);
      reset();
      onPublished();
    } catch (e) {
      // The config-service 409 comes through as a clear message ("version already exists").
      toast.push("error", e instanceof Error ? e.message : "Could not publish the release.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <div className="section-head" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Smartphone size={16} /> Publish a new {APP_LABEL[app]} build
        </h2>
      </div>
      <form className="form" onSubmit={onSubmit}>
        <div className="row wrap" style={{ gap: 16, alignItems: "flex-start" }}>
          <label style={{ flex: "1 1 260px" }}>
            APK file
            <input
              ref={fileRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? <span className="faint" style={{ fontSize: 11 }}>{file.name} · {fmtBytes(file.size)}</span> : null}
          </label>
          <label style={{ flex: "0 1 160px" }}>
            Version code
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder="e.g. 42"
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
            />
          </label>
          <label style={{ flex: "0 1 200px" }}>
            Version name
            <input
              type="text"
              placeholder="e.g. 1.4.2"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
            />
          </label>
        </div>
        <label>
          Release notes
          <textarea
            rows={3}
            placeholder="What changed in this build (shown on the app's What's New screen)…"
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
          />
        </label>
        <div className="row between" style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label className="row" style={{ gap: 8, alignItems: "center", margin: 0 }}>
            <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
            <span>Force update (mandatory)</span>
          </label>
          <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
            <Upload size={14} /> {submitting ? "Publishing…" : "Publish release"}
          </button>
        </div>
      </form>
    </div>
  );
}
